import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatPercentage, useSelect, useSwitch, useViewport } from '@chilecompra/react-kit'
import { useTheme } from 'styled-components'
import { Card, DataTable, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import HorizontalBarChart from '../../components/Charts/HorizontalBarChart'
import RegionalPurchasingMechanisms from '../../components/Infographics/RegionalPurchasingMechanisms'
import generateYearsBetween from '../../modules/utils/years'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import { onGetPurchasesSectorThunk } from './ContingenciesPurchasesSectorSection.actions'
import { Content, GridContainer, GridItem, Info, Italic, Root } from './ContingenciesPurchasesSectorSection.styles'

/**
 * The ContingenciesPurchasesSectorSection's container.
 */
const ContingenciesPurchasesSectorSection = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const {
    size: { isUpToMedium }
  } = useViewport()

  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { categories, csv, data, loading } = useSelector(state => state.contingenciesPurchasesSector)

  const { contingencyOptions, contingencySelected } = useSelector(state => state.contingencies)
  const [startYear, setStartYear] = useState(undefined)
  const [endYear, setEndYear] = useState(undefined)
  const getYearOptions = contingencyOptions?.find(option => option.value === contingencySelected)
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => dispatch(onGetPurchasesSectorThunk({ year: updatedValue, contingencySelected })),
    overwriteCallback: () => endYear
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () => exportToImage('purchases-sector', `Info_Compras_Sector_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Compras_Sector_${yearValue}`)
  })

  const getEnhancedData = useCallback(
    (currency, collection = []) =>
      collection.map(datum => ({
        ...datum,
        color: theme.palette.charts[datum.colorName],
        y: currency ? datum.amountUSD : datum.amount
      })),
    []
  )

  const handleDataLabelFormatter = useCallback(
    function () {
      return `
      <strong>
        ${currencyValue ? CURRENCY_VALUES.USD : CURRENCY_VALUES.CLP} ${formatAmount(this.y)} (${formatPercentage(
          this.point.percentage
        )})
      </strong>
      <br />
      <span style="color: ${theme.palette.brand.black3};">
        ${formatAmount(this.point.orders)} órdenes de compra
      </span>
    `
    },
    [currencyValue]
  )

  useEffect(() => {
    setStartYear(getYearOptions?.startYear)
    setEndYear(getYearOptions?.endYear)
  }, [getYearOptions])

  useEffect(() => {
    if (!endYear) {
      return
    }

    dispatch(onGetPurchasesSectorThunk({ year: endYear, contingencySelected }))
  }, [endYear])

  return (
    <Root>
      <Content>
        <GridContainer>
          <GridItem xs={12} md={6}>
            <Typography
              color="primary"
              fontSize="18px"
              fontWeight="bold"
              lineHeight="21px"
              margin={isUpToMedium ? '96px 0 0' : '48px 0 24px'}
              variant="body1"
            >
              Sectores del Estado
            </Typography>
            <Typography
              fontSize="34px"
              fontWeight="bold"
              lineHeight="38px"
              margin={isUpToMedium ? '12px 0' : '0'}
              variant="h2"
              width={isUpToMedium ? '485px' : undefined}
            >
              Compras públicas de cada sector por COVID-19
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={isUpToMedium ? 5 : 12}>
            <Typography
              fontSize="18px"
              lineHeight="24px"
              margin={isUpToMedium ? '0' : '24px 0'}
              variant="body1"
              width={isUpToMedium ? '448px' : undefined}
            >
              Conoce el detalle de montos transados, número de órdenes de compra y cantidad de organismos compradores
              por sector que realizaron compras por COVID-19.
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer margin="24px 0 96px">
          <GridItem xs={12}>
            <DataLayout
              disableCaptureImage={disableCaptureImage}
              disableDownloadFile={disableDownloadFile}
              header={
                <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                  <GridItem md={2} xs={12}>
                    <Select
                      label="AÑO"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem md={2} xs={12}>
                    <Switch
                      checked={currencyValue}
                      color="primary"
                      labelLeft="CLP"
                      labelRight="USD"
                      margin="0 0 0 30px"
                      onChange={handleChangeCurrency}
                    />
                  </GridItem>
                </GridContainer>
              }
              infographic={<RegionalPurchasingMechanisms />}
              noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
              ref={dataLayoutRef}
              updatedDate={latestUpdate}
              onCaptureImage={handleCaptureImage}
              onChangeView={handleChangeView}
              onDownloadFile={handleDownloadFile}
            >
              <DataLayoutChild view={DataLayoutViews.CHART}>
                <Card color="blank" elevation="1" margin="0 0 32px">
                  <GridContainer>
                    <GridItem xs={12}>
                      <HorizontalBarChart
                        categories={categories}
                        data={getEnhancedData(currencyValue, data)}
                        dataLabelFormatter={handleDataLabelFormatter}
                        id="purchases-sector"
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer margin="19px 0 0">
                    <GridItem xs={12} container justifyContent="space-between">
                      <Info>
                        <Italic>Los montos transados corresponden a montos brutos (monto neto + impuestos).</Italic>
                      </Info>
                      <Info>{!currencyValue ? 'Millones de pesos (CLP)' : ' Miles de dólares (USD)'}</Info>
                    </GridItem>
                  </GridContainer>
                </Card>
              </DataLayoutChild>
              <DataLayoutChild view={DataLayoutViews.TABLE}>
                <GridContainer padding="0 0 24px">
                  <GridItem xs={12}>
                    <Card color="blank" elevation="1" margin="0 0 32px">
                      <DataTable
                        headers={[
                          { label: 'Sectores', value: 'name' },
                          { label: 'Nº de órdenes de compra', value: 'orders' },
                          { label: 'Monto total', value: 'amount' },
                          { label: 'Monto total (USD)', value: 'amountUSD' }
                        ]}
                        headersCellsProps={[
                          { enableSort: true, width: '50%' },
                          { enableSort: true, width: '20%' },
                          { enableSort: true },
                          { enableSort: true }
                        ]}
                        initialRows={6}
                        loading={loading}
                        records={getEnhancedData(currencyValue, data)}
                        recordsDecorators={[
                          ({ name }) => name,
                          ({ orders }) => formatAmount(orders),
                          ({ amount }) => `$ ${formatAmount(amount)}`,
                          ({ amountUSD }) => `$USD ${formatAmount(amountUSD)}`
                        ]}
                      />
                    </Card>
                  </GridItem>
                </GridContainer>
                <GridContainer margin="30px 0 0">
                  <GridItem xs={12}>
                    <Info>
                      <Italic>
                        La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto
                        + impuestos)
                      </Italic>
                    </Info>
                  </GridItem>
                </GridContainer>
              </DataLayoutChild>
            </DataLayout>
          </GridItem>
        </GridContainer>
      </Content>
    </Root>
  )
}

export default ContingenciesPurchasesSectorSection
